import { useState } from "react";
import type { SortKey } from "@/types";

export interface FilterState {
  keyword: string;
  genres: string[];
  directors: string[];
  decades: string[];
}

interface FilterBarProps {
  options: {
    genres: string[];
    directors: string[];
    decades: string[];
  };
  value: FilterState;
  onChange: (state: FilterState) => void;
  sort: SortKey;
  onSortChange: (sort: SortKey) => void;
  onReset: () => void;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "按时间" },
  { key: "popular", label: "按热门" },
  { key: "rating", label: "按评分" },
];

export default function FilterBar({ options, value, onChange, sort, onSortChange, onReset }: FilterBarProps) {
  const [panelOpen, setPanelOpen] = useState(false);

  const toggle = (list: string[], item: string) =>
    list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item];

  const activeCount = value.genres.length + value.directors.length + value.decades.length + (value.keyword ? 1 : 0);

  return (
    <div className="rounded-lg border border-background-300/70 bg-background-50 p-4 md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-500">
            <i className="ri-filter-3-line text-lg" />
          </span>
          <span className="text-sm font-medium text-foreground-800">筛选条件</span>
          {activeCount > 0 && (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
              已选 {activeCount} 项
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-full border border-background-300 bg-background-100 px-1 py-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onSortChange(option.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  sort === option.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onReset}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 px-3 py-2 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-200"
          >
            <i className="ri-refresh-line" />
            重置
          </button>
          <button
            type="button"
            onClick={() => setPanelOpen((open) => !open)}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 px-3 py-2 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-200 md:hidden"
          >
            <i className={panelOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            展开筛选
          </button>
        </div>
      </div>

      <div className={`${panelOpen ? "block" : "hidden"} mt-5 flex-col gap-5 md:flex md:flex-col`}>
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-foreground-400" htmlFor="filter-keyword">
            剧情关键词
          </label>
          <input
            id="filter-keyword"
            value={value.keyword}
            onChange={(event) => onChange({ ...value, keyword: event.target.value })}
            type="text"
            name="filter_keyword"
            placeholder="例如：都市、案件、古装、情感"
            className="mt-2 h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-foreground-400">年代</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {options.decades.map((decade) => (
              <button
                key={decade}
                type="button"
                onClick={() => onChange({ ...value, decades: toggle(value.decades, decade) })}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  value.decades.includes(decade)
                    ? "border-primary-300 bg-primary-100 text-primary-700"
                    : "border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300"
                }`}
              >
                {decade === "1990s" ? "90年代" : decade === "1980s" ? "80年代" : "2000年后"}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-foreground-400">类型</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {options.genres.map((genre) => (
              <button
                key={genre}
                type="button"
                onClick={() => onChange({ ...value, genres: toggle(value.genres, genre) })}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  value.genres.includes(genre)
                    ? "border-accent-300 bg-accent-100 text-accent-900"
                    : "border-background-300 bg-background-100 text-foreground-600 hover:border-accent-300"
                }`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-foreground-400">导演</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {options.directors.map((director) => (
              <button
                key={director}
                type="button"
                onClick={() => onChange({ ...value, directors: toggle(value.directors, director) })}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  value.directors.includes(director)
                    ? "border-secondary-400 bg-secondary-200 text-secondary-900"
                    : "border-background-300 bg-background-100 text-foreground-600 hover:border-secondary-400"
                }`}
              >
                {director}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}